/**
 * The host-settings draft — the "persist host state before finish" half of O-onboarding-finish
 * (#576 ordinal 19).
 *
 * Step 3 of onboarding collects the payday type and weekday, whether a
 * parent approves each completed quest, and the family's weekend-grace setting. Until this change the
 * route held all three in React state alone and sent them only from `handleFinish`, so a refresh
 * before the ceremony button dropped every one of them without a word.
 *
 * This is deliberately the SAME shape as `childDraft.js` and `signupDraft.js`, and not a
 * generalisation of them. The three differ where it matters — a different key, a different
 * allowlist, a different clearing rule — and one module serving all three would carry every
 * difference as a parameter. The rules that make any of them safe are repeated here on purpose:
 *
 *   1. ONLY THE KNOWN FIELDS ARE STORED, on the way in AND on the way out, so a future field
 *      cannot arrive in storage by accident and bytes from an older build cannot reach the form.
 *   2. EVERY FIELD IS CHECKED FOR WHAT IT MUST BE, not merely for its name. Here that is a RANGE
 *      check as well as a type check, and the range is the load-bearing half: `paydayDay` is
 *      handed to `paydayPeriodFromDayNumber` (`../lib/payday.js`), which THROWS for anything that
 *      is not an integer 0-6. A stored `9` is a number, so a `typeof` test passes it — and the
 *      family then presses the ceremony button and gets an error every single time, with no way
 *      out but clearing their browser storage. The module that promises corrupt bytes cannot reach
 *      the form has to be the thing that checks the range.
 *   3. EVERY ENTRY POINT IS TOTAL. Storage can be denied (private mode) and stored bytes can be
 *      corrupt. Losing a draft is a small disappointment; throwing into a render is a lost family.
 *   4. THE DRAFT IS SCOPED TO THE FAMILY THAT WROTE IT. The key carries the tenant id, because
 *      `sessionStorage` outlives a sign-out within the same tab and this app clears none of it on
 *      the way out. Without the scope, the next family to sign in on that tab would inherit the
 *      previous family's payday.
 *
 * `sessionStorage`, not `localStorage`: these are household settings on what may be a shared
 * machine, and a draft must not outlive the browser session.
 *
 * A FALSY VALUE IS REAL CONTENT HERE, which is the one way this module is not like its siblings.
 * Sunday is `0` and "no approval needed" is `false`. Neither may be treated as "nothing chosen",
 * so every emptiness test below asks whether a field is PRESENT, never whether it is truthy.
 */

/** @type {string} The key prefix. The tenant id is appended, so one family cannot read another's. */
export const HOST_DRAFT_KEY_PREFIX = 'chorestory.onboarding.host';

/** The lowest and highest weekday `DayPicker` can emit, and `paydayPeriodFromDayNumber` accepts. */
const MIN_DAY = 0;
const MAX_DAY = 6;

/** The toggles, which must each be a real boolean. */
const BOOLEAN_FIELDS = ['approvalRequired', 'weekendGrace'];

/**
 * An empty draft, which is also what a denied, absent or corrupt read returns.
 *
 * `null` for each field means "the family never picked", which is exactly what the route's own
 * state means by `null` — so a restored empty draft leaves the screen showing the family's SAVED
 * answer rather than a guess.
 */
export const EMPTY_HOST_DRAFT = {
  paydayDay: null,
  approvalRequired: null,
  weekendGrace: null,
};

/**
 * The storage key for one family.
 *
 * @param {string} tenantId the family's own id
 * @returns {string|null} the key, or `null` when there is no usable tenant id — which every entry
 *                        point below treats as "no storage", rather than writing to a shared key
 */
export function hostDraftKey(tenantId) {
  if (typeof tenantId !== 'string' || tenantId.trim() === '') return null;
  return `${HOST_DRAFT_KEY_PREFIX}.${tenantId}`;
}

/** Whether a value is a weekday this app can actually turn into a payday period. */
function isWeekday(value) {
  return typeof value === 'number' && Number.isInteger(value) && value >= MIN_DAY && value <= MAX_DAY;
}

/**
 * Keep the allowlisted fields, and keep each one ONLY when it is what it must be.
 *
 * A wrong field is dropped rather than repaired. Clamping a stored `9` to `6` would tell a family
 * they settle up on Saturday when they never said so; an untouched control is honest about having
 * lost something.
 */
function pick(source) {
  const out = {};
  if (!source || typeof source !== 'object' || Array.isArray(source)) return out;
  if (isWeekday(source.paydayDay)) out.paydayDay = source.paydayDay;
  if (['weekly', 'monthly', 'ad_hoc'].includes(source.paydayType)) out.paydayType = source.paydayType;
  for (const field of BOOLEAN_FIELDS) {
    if (typeof source[field] === 'boolean') out[field] = source[field];
  }
  return out;
}

/**
 * Store one family's draft. A draft holding no choice at all clears the key instead of writing an
 * empty object, so a parent who never touched step 3 leaves nothing behind — and so the write a
 * freshly mounted screen makes cannot resurrect what a finish just cleared.
 *
 * @param {string} tenantId the family's own id; a missing one stores nothing
 * @param {Object} draft the whole choice state; anything outside the allowlist is dropped
 */
export function writeHostDraft(tenantId, draft) {
  const key = hostDraftKey(tenantId);
  if (key === null) return;
  const kept = pick(draft);
  // PRESENCE, never truthiness: Sunday is `0` and "no approval needed" is `false`.
  if (Object.keys(kept).length === 0) {
    clearHostDraft(tenantId);
    return;
  }
  try {
    window.sessionStorage.setItem(key, JSON.stringify(kept));
  } catch {
    /* Storage denied. The screen still works; only the draft is lost. */
  }
}

/**
 * Read one family's draft back, merged onto the empty shape so every field is a defined value of
 * the right type — a weekday, a boolean, or `null` for "never picked".
 *
 * @param {string} tenantId the family's own id; a missing one reads nothing
 * @returns {{paydayDay: number|null, approvalRequired: boolean|null, weekendGrace: boolean|null}}
 *          Never throws.
 */
export function readHostDraft(tenantId) {
  const key = hostDraftKey(tenantId);
  if (key === null) return { ...EMPTY_HOST_DRAFT };
  try {
    const raw = window.sessionStorage.getItem(key);
    if (!raw) return { ...EMPTY_HOST_DRAFT };
    return { ...EMPTY_HOST_DRAFT, ...pick(JSON.parse(raw)) };
  } catch {
    return { ...EMPTY_HOST_DRAFT };
  }
}

/**
 * Forget one family's draft. Called once the settings are actually saved, and never before.
 *
 * **It VERIFIES the removal rather than assuming it.** The obvious version swallows a failed
 * `removeItem` on the reasoning that a denied store holds nothing anyway. That reasoning is wrong,
 * and the cost was measured rather than argued: seed a draft, let ONLY `removeItem` throw, save
 * successfully, then mount again. The family asked for Friday, the server stored Monday, and the
 * screen came back showing **Friday** — the stale draft beat the server's own answer, which is the
 * one thing this route promises never to do (`OnboardingRoute.jsx`, "Publish the SERVER's answer").
 *
 * So a removal that did not take is overwritten with a value the reader already treats as absent.
 *
 * KNOWN RESIDUAL, stated rather than hidden: if `removeItem` AND `setItem` both fail while
 * `getItem` still works, the stale draft survives. Nothing this module can do reaches that case,
 * and no browser is known to behave that way.
 */
export function clearHostDraft(tenantId) {
  const key = hostDraftKey(tenantId);
  if (key === null) return;
  try {
    window.sessionStorage.removeItem(key);
  } catch {
    /* Removal refused. Fall through to the overwrite below, which may still work. */
  }
  try {
    // `readHostDraft` returns the empty draft for any falsy raw value, so an empty string is a
    // tombstone the reader cannot mistake for a choice.
    if (window.sessionStorage.getItem(key)) window.sessionStorage.setItem(key, '');
  } catch {
    /* Storage is denied outright. A read will fail the same way, so nothing is restored from it. */
  }
}
